import { type GachaMachineView, isWatchGachaMachine } from "./gacha-data"
import packArtMap from "./gacha-pack-art-map.json"

export type GachaSeriesView = {
  slug: string
  name: string
  eyebrow: string
  description: string
  image: string
  videoSrc: string
  videoHevc: string
  machines: readonly GachaMachineView[]
}

type GachaCategoryConfig = {
  name: string
  eyebrow: string
  description: string
  image?: string
  videoSrc?: string
  videoHevc?: string
  machineCodes: readonly string[]
  featuredMachineCodes?: readonly string[]
}

type GachaPackArtMap = {
  categoryOrder: readonly string[]
  commonStockInstantBuybackThresholds?: Record<string, number>
  categories: Record<string, GachaCategoryConfig>
  machineImages: Record<string, string>
  machineVideos: Record<string, string>
  watchReveal: {
    openedBoxVideos: Record<string, string>
    glowVideos: Record<string, string>
  }
}

const typedPackArtMap = packArtMap as GachaPackArtMap

export const categoryOrder: readonly string[] = typedPackArtMap.categoryOrder
const categoryConfigMap: Record<string, GachaCategoryConfig> =
  typedPackArtMap.categories
const machineImageMap: Record<string, string> = typedPackArtMap.machineImages
const commonStockInstantBuybackThresholdMap =
  typedPackArtMap.commonStockInstantBuybackThresholds ?? {}
const configuredMachineCodes = new Set([
  ...Object.values(categoryConfigMap).flatMap((category) =>
    category.machineCodes.map((code) => code),
  ),
  ...Object.keys(machineImageMap),
])
export const DEFAULT_COMMON_STOCK_INSTANT_BUYBACK_THRESHOLD = 50

export function getMachinePrice(machine: GachaMachineView) {
  return Number.isFinite(machine.price)
    ? machine.price
    : Number.MAX_SAFE_INTEGER
}

export function getLowestMachinePriceLabel(
  machines: readonly GachaMachineView[],
) {
  const lowestMachine = machines.reduce<GachaMachineView | null>(
    (lowest, machine) => {
      if (!Number.isFinite(machine.price)) return lowest

      if (!lowest || machine.price < lowest.price) return machine
      return lowest
    },
    null,
  )

  return lowestMachine?.priceLabel ?? null
}

export function sortGachaMachines(
  machines: readonly GachaMachineView[],
  featuredMachineCodes: readonly string[] = [],
) {
  const featuredMachineOrder = new Map(
    featuredMachineCodes.map((code, index) => [code, index]),
  )

  return [...machines].sort((a, b) => {
    const aFeaturedIndex = featuredMachineOrder.get(a.code)
    const bFeaturedIndex = featuredMachineOrder.get(b.code)

    if (aFeaturedIndex !== undefined && bFeaturedIndex !== undefined) {
      return aFeaturedIndex - bFeaturedIndex
    }
    if (aFeaturedIndex !== undefined) return -1
    if (bFeaturedIndex !== undefined) return 1

    return getMachinePrice(a) - getMachinePrice(b)
  })
}

export function getSeriesCategoryImage(
  seriesSlug: string,
  fallbackImage: string,
) {
  return categoryConfigMap[seriesSlug]?.image ?? fallbackImage
}

export function getSeriesCategoryVideo(seriesSlug: string) {
  return categoryConfigMap[seriesSlug]?.videoSrc ?? ""
}

export function getMachinePackImage(machine: GachaMachineView) {
  const mappedImage = machineImageMap[machine.code]
  if (mappedImage) {
    return { image: mappedImage, source: "config" as const }
  }

  return { image: machine.thumbnailUrl, source: "api" as const }
}

export function getMachinePackPreview(machine: GachaMachineView): {
  image: string
  videoSrc?: string
  source: "config" | "api"
} {
  if (isWatchGachaMachine(machine.code) && machine.videoSrc) {
    return {
      image: machine.thumbnailUrl,
      videoSrc: machine.videoSrc,
      source: "config" as const,
    }
  }

  return getMachinePackImage(machine)
}

export function getMachineSeries(machine: GachaMachineView) {
  for (const slug of categoryOrder) {
    if (slug === "collectibles") continue

    const config = categoryConfigMap[slug]
    if (!config) continue

    if (config.machineCodes.includes(machine.code)) {
      return slug
    }
  }

  return "collectibles"
}

export function isConfiguredGachaMachineCode(machineCode: string) {
  return configuredMachineCodes.has(machineCode)
}

export function resolveCommonStockInstantBuybackThreshold(
  machineCode: string | undefined,
  thresholds: Record<string, number>,
) {
  if (!machineCode) return DEFAULT_COMMON_STOCK_INSTANT_BUYBACK_THRESHOLD

  const configuredThreshold = thresholds[machineCode]
  return Number.isFinite(configuredThreshold)
    ? configuredThreshold
    : DEFAULT_COMMON_STOCK_INSTANT_BUYBACK_THRESHOLD
}

export function getCommonStockInstantBuybackThreshold(machineCode?: string) {
  return resolveCommonStockInstantBuybackThreshold(
    machineCode,
    commonStockInstantBuybackThresholdMap,
  )
}

export function getSeriesCopy(slug: string) {
  return categoryConfigMap[slug] ?? categoryConfigMap.collectibles
}

export function isFeaturedGachaMachine(
  seriesSlug: string,
  machineCode: string,
) {
  return (
    categoryConfigMap[seriesSlug]?.featuredMachineCodes?.includes(
      machineCode,
    ) ?? false
  )
}

export function buildGachaSeries(machines: readonly GachaMachineView[]) {
  const grouped = new Map<string, GachaMachineView[]>()

  for (const machine of machines) {
    const slug = getMachineSeries(machine)
    grouped.set(slug, [...(grouped.get(slug) ?? []), machine])
  }

  return categoryOrder
    .map((slug): GachaSeriesView | null => {
      const copy = getSeriesCopy(slug)
      const seriesMachines = sortGachaMachines(
        grouped.get(slug) ?? [],
        copy.featuredMachineCodes,
      )

      if (!seriesMachines.length) return null

      const firstMachine = seriesMachines[0]

      return {
        slug,
        ...copy,
        image: getSeriesCategoryImage(slug, firstMachine.thumbnailUrl),
        videoSrc: getSeriesCategoryVideo(slug),
        videoHevc: copy.videoHevc ?? "",
        machines: seriesMachines,
      }
    })
    .filter((series): series is GachaSeriesView => series !== null)
}

export function getSortedSeriesMachines(series: GachaSeriesView) {
  return sortGachaMachines(
    series.machines,
    getSeriesCopy(series.slug).featuredMachineCodes,
  )
}

export function getMachineUnavailableLabel(machine: GachaMachineView) {
  if (!machine.isOpen) return "Closed"
  if (machine.isSoldOut) return "Out of stock"
  return ""
}

export function getPackAvailabilityText(
  machine: Pick<GachaMachineView, "isOpen" | "isSoldOut">,
) {
  if (!machine.isOpen) return " · Closed"
  if (machine.isSoldOut) return " · Out of stock"
  return ""
}

export function getDefaultMachineCode(machines: readonly GachaMachineView[]) {
  return (
    machines.find((machine) => !getMachineUnavailableLabel(machine))?.code ??
    machines[0]?.code ??
    null
  )
}
